import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';

import { InteractablePopover } from 'ui-components/components/interactable-popover';

import UserTimeFormat from '../../../components/user-time-format/connected';
import UserDateFormat from '../../../components/user-date-format/index';

import style from './icon-message-column.module.less';

export const emptyState = 'empty';
export const errorState = 'error';
export const normalState = 'normal';

const messages = defineMessages({
  notificationErrorMessageTitle: {
    description: 'default error message',
    defaultMessage: 'Shipment Error',
    id: 'VEEizm',
  },
  failedMessage: {
    defaultMessage: 'Failed',
    description: 'iconMessageColumn.failed',
    id: 'SvRx6/',
  },
});

const defaultTitle = <FormattedMessage {...messages.notificationErrorMessageTitle} />;

/**
 * @param {object} props
 * @param {import('@fortawesome/fontawesome-common-types').IconDefinition} props.icon
 * @param {React.ReactNode | string} props.message
 * @param {string} [props.state]
 * @param {React.ReactNode | string} [props.tooltipMessage]
 * @param {string} [props.date]
 * @param {import('../../../prop-types/intl').FormattedMessagePropType} [props.popoverFlyoutTitle]
 * @returns {JSX.Element}
 */
const IconMessageColumn = ({
  icon,
  message,
  state = normalState,
  tooltipMessage = null,
  date = null,
  popoverFlyoutTitle = defaultTitle,
}) => {
  const classNames = classnames(style['icon-message-column'], {
    [style.error]: state === errorState,
  });
  const content = state === errorState ? <FormattedMessage {...messages.failedMessage} /> : message;

  if (state === emptyState) {
    return (
      <div className={classnames(style['icon-message-column'], style.empty)}>
        <div className={style['default-icon']}>
          <FontAwesomeIcon icon={icon} />
        </div>
      </div>
    );
  }

  return (
    <div className={classNames}>
      <InteractablePopover
        ignoreFlyout={state === normalState}
        popoverBody={[tooltipMessage?.props?.defaultMessage || tooltipMessage]}
        preferPlace='bottom'
        title={popoverFlyoutTitle}
        useMessagesForTooltipText={state === normalState}
      >
        {state === normalState && (
          <div className={style['default-icon']}>
            <FontAwesomeIcon icon={icon} />
          </div>
        )}
        {state === errorState && (
          <div className={style['error-container']}>
            <div className={style['error-icon']} data-testid='error-icon'>
              <FontAwesomeIcon icon={faExclamationCircle} />
            </div>
            <div className={classnames(style.message, style.error)}>{content}</div>
          </div>
        )}
      </InteractablePopover>
      {state === normalState && (
        <div className={style.message}>
          {content}
          {date && (
            <span className={style.date}>
              <UserDateFormat value={date} />
              <span className={style['order-note-footer-bullet']}>&bull;</span>
              <UserTimeFormat value={date} />
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(IconMessageColumn);
